import { api } from '@/lib/axios';

export type CategorieImmo = 'CONSTRUCTIONS' | 'MATERIEL' | 'AUTRES';
export type ImmobilisationStatut = 'EN_SERVICE' | 'CEDE' | 'MIS_AU_REBUT';
export type ModeSortie = 'CESSION' | 'REBUT';
export type ModeFinancement = 'FONDS_PROPRES' | 'CREDIT' | 'CREDIT_BAIL';

export type Immobilisation = {
  id: string;
  code: string;
  libelle: string;
  categorie: CategorieImmo;
  compte_immobilisation: string;
  compte_amortissement: string;
  fournisseur: string | null;
  facture_source_id: string | null;
  engin_source_id: string | null;
  date_acquisition: string | null;
  date_mise_en_service: string | null;
  valeur_acquisition_ht: number;
  tva_deductible: number;
  mode_financement: ModeFinancement;
  valeur_residuelle: number;
  duree_mois: number;
  methode: 'LINEAIRE';
  statut: ImmobilisationStatut;
  notes: string | null;
};

export type LignePlan = { annee: number; mois: number; dotation: number; cumul: number; vnc: number };

export type ImmobilisationInput = {
  code: string;
  libelle: string;
  categorie: CategorieImmo;
  fournisseur?: string | null;
  date_acquisition?: string | null;
  date_mise_en_service?: string | null;
  valeur_acquisition_ht: number;
  tva_deductible?: number;
  mode_financement?: ModeFinancement;
  valeur_residuelle?: number;
  duree_mois?: number;
  notes?: string | null;
};

/* ----------------------------------------------------------- Crédit-bail */
export type TraitementLeasing = 'IMMOBILISATION' | 'LOCATION_SIMPLE';
export type NatureApport = 'ACOMPTE' | 'PREMIER_LOYER_MAJORE';
export type LeasingStatut = 'EN_COURS' | 'SOLDE' | 'OPTION_LEVEE' | 'RESILIE';
export type EcheanceStatut = 'A_VENIR' | 'PAYEE';

export type EcheanceLeasing = {
  id: string; numero: number; date: string; montant: number;
  part_capital: number; part_interets: number; capital_restant: number;
  statut: EcheanceStatut; ecriture_id: string | null;
};
export type Leasing = {
  id: string; immobilisation_id: string | null; organisme_bailleur: string | null;
  valeur_financee: number; apport_montant: number; apport_nature: NatureApport;
  duree_mois: number; echeance_mensuelle: number; taux_annuel: number;
  date_debut: string | null; date_levee_option: string | null; valeur_residuelle_option: number;
  traitement: TraitementLeasing; statut: LeasingStatut; notes: string | null;
  echeances: EcheanceLeasing[]; dette_residuelle: number;
};
export type LeasingInput = {
  immobilisation_id?: string | null; organisme_bailleur?: string | null;
  valeur_financee: number; apport_montant?: number; apport_nature?: NatureApport;
  duree_mois: number; echeance_mensuelle?: number; taux_annuel?: number;
  date_debut?: string | null; date_levee_option?: string | null; valeur_residuelle_option?: number;
  traitement?: TraitementLeasing; notes?: string | null;
};

export async function listLeasing(): Promise<Leasing[]> {
  const { data } = await api.get<Leasing[]>('/leasing');
  return data;
}
export async function getLeasing(id: string): Promise<Leasing> {
  const { data } = await api.get<Leasing>(`/leasing/${id}`);
  return data;
}
export async function createLeasing(payload: LeasingInput): Promise<Leasing> {
  const { data } = await api.post<Leasing>('/leasing', payload);
  return data;
}
export async function comptabiliserAcquisitionLeasing(id: string): Promise<{ detail: string }> {
  const { data } = await api.post<{ detail: string }>(`/leasing/${id}/comptabiliser-acquisition`);
  return data;
}
export async function payerEcheanceLeasing(echeanceId: string): Promise<{ detail: string }> {
  const { data } = await api.post<{ detail: string }>(`/leasing/echeances/${echeanceId}/payer`);
  return data;
}
export async function deleteLeasing(id: string): Promise<void> {
  await api.delete(`/leasing/${id}`);
}

export type Cession = {
  id: string;
  immobilisation_id: string;
  mode: ModeSortie;
  date_cession: string;
  prix_cession: number;
  valeur_brute: number;
  cumul_amortissement: number;
  vnc: number;
  plus_ou_moins_value: number;
  ecriture_id: string | null;
  motif: string | null;
};

export type CessionInput = { mode: ModeSortie; date_cession: string; prix_cession?: number; motif?: string | null };

export type BienRecon = {
  id: string; code: string; categorie: string; brut: string;
  dotation_exercice: string; cumul_fin_exercice: string; vnc_fin_exercice: string;
};
export type Reconciliation = {
  annee: number;
  plan_dotations_exercice: string;
  debit_681: string; credit_28x: string;
  ecart_dotation: string; ecart_equilibre: string;
  total_brut_21x: string; total_cumul_28x: string; total_vnc: string;
  biens: BienRecon[];
  biens_sans_duree: { id: string; code: string; libelle: string }[];
};

export type CertificationPlan = { plan_certifie: boolean; problemes: string[] };
export type ResultatDotation = {
  plan_certifie: boolean; problemes?: string[];
  dotations_creees?: number; dotations_deja?: number; sans_duree?: number; hors_plan?: number;
};
export type ResultatCapitalisation = {
  engins_examines: number; inscrits: number; deja_inscrits: number;
  ouvertures_comptabilisees: number; sans_valeur: number;
};

export async function listImmobilisations(): Promise<Immobilisation[]> {
  const { data } = await api.get<Immobilisation[]>('/immobilisations');
  return data;
}
export async function getImmobilisation(id: string): Promise<Immobilisation> {
  const { data } = await api.get<Immobilisation>(`/immobilisations/${id}`);
  return data;
}
export async function createImmobilisation(payload: ImmobilisationInput): Promise<Immobilisation> {
  const { data } = await api.post<Immobilisation>('/immobilisations', { methode: 'LINEAIRE', ...payload });
  return data;
}
export async function updateImmobilisation(id: string, payload: Partial<ImmobilisationInput>): Promise<Immobilisation> {
  const { data } = await api.put<Immobilisation>(`/immobilisations/${id}`, payload);
  return data;
}
export async function deleteImmobilisation(id: string): Promise<void> {
  await api.delete(`/immobilisations/${id}`);
}
export async function getPlan(id: string): Promise<LignePlan[]> {
  const { data } = await api.get<LignePlan[]>(`/immobilisations/${id}/plan`);
  return data;
}
export async function certifierPlan(): Promise<CertificationPlan> {
  const { data } = await api.get<CertificationPlan>('/immobilisations/certifier');
  return data;
}
export async function getReconciliation(annee: number): Promise<Reconciliation> {
  const { data } = await api.get<Reconciliation>('/immobilisations/reconciliation', { params: { annee } });
  return data;
}
export async function genererDotations(mois: number, annee: number): Promise<ResultatDotation> {
  const { data } = await api.post<ResultatDotation>('/immobilisations/generer', null, { params: { mois, annee } });
  return data;
}
export async function ceder(id: string, payload: CessionInput): Promise<Cession> {
  const { data } = await api.post<Cession>(`/immobilisations/${id}/ceder`, payload);
  return data;
}
export async function getCession(id: string): Promise<Cession | null> {
  const { data } = await api.get<Cession | null>(`/immobilisations/${id}/cession`);
  return data;
}
export async function listCessions(annee?: number): Promise<Cession[]> {
  const { data } = await api.get<Cession[]>('/immobilisations/cessions', { params: annee ? { annee } : undefined });
  return data;
}
export async function capitaliserParc(): Promise<ResultatCapitalisation> {
  const { data } = await api.post<ResultatCapitalisation>('/immobilisations/capitaliser-parc');
  return data;
}

/** Cumul d'amortissement couru jusqu'au mois (annee, mois) inclus, lu dans le plan. */
export function cumulADate(plan: LignePlan[], annee: number, mois: number): number {
  const lignes = plan.filter((p) => p.annee < annee || (p.annee === annee && p.mois <= mois));
  return lignes.length ? Number(lignes[lignes.length - 1].cumul) : 0;
}

/** Agrège le plan mensuel par exercice : dotation de l'année, cumul et VNC de fin d'année. */
export function planParAnnee(plan: LignePlan[]): { annee: number; dotation: number; cumul: number; vnc: number }[] {
  const par = new Map<number, { annee: number; dotation: number; cumul: number; vnc: number }>();
  for (const p of plan) {
    const cur = par.get(p.annee) ?? { annee: p.annee, dotation: 0, cumul: 0, vnc: 0 };
    cur.dotation += Number(p.dotation);
    cur.cumul = Number(p.cumul);  // dernière valeur de l'année
    cur.vnc = Number(p.vnc);
    par.set(p.annee, cur);
  }
  return [...par.values()].sort((a, b) => a.annee - b.annee);
}
